/**
 * SessionPage - Main page for facilitator sessions.
 *
 * Design: DENiZEN
 * - Owns its own GreenPageLayout wrapper; SessionFlow renders inside the stone card
 * - WCAG 2.1 AA compliant
 */

import { useState, useCallback, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { GreenPageLayout } from '@/components/GreenPageLayout'
import type { GroundingImage } from '@/components/ScrollingPebbleBackground'
import { SessionProvider } from '@/context/SessionContext'
import { SessionFlow } from './SessionFlow'
import type { SessionFlowStep } from './SessionFlow'
import { trackApiError } from '@/services/telemetry'

export interface SessionPageProps {
  /**
   * Facilitator ID (from auth or URL param)
   */
  facilitatorId?: string
}

const API_BASE = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? ''

const PEBBLE_STEPS: SessionFlowStep[] = ['start', 'config', 'consent', 'mic_test']

export function SessionPage({ facilitatorId = '550e8400-e29b-41d4-a716-446655440000' }: SessionPageProps) {
  const location = useLocation()
  const fromEventCode = !!(location.state as { fromEventCode?: boolean } | null)?.fromEventCode

  const [bgImages, setBgImages] = useState<GroundingImage[]>([])
  const [showPebbles, setShowPebbles] = useState(true)

  const handleGroundingImagesReady = useCallback((images: GroundingImage[]) => {
    setBgImages(images)
  }, [])

  const handleStepChange = useCallback((step: SessionFlowStep) => {
    setShowPebbles(PEBBLE_STEPS.includes(step))
  }, [])

  // Seed background pebbles with community images on mount.
  // Uses a functional update so grounding images from handleGroundingImagesReady
  // are never overwritten if they arrive before the fetch resolves.
  useEffect(() => {
    const controller = new AbortController()
    fetch(`${API_BASE}/api/v1/images/random?count=24`, { signal: controller.signal })
      .then(r => {
        if (!r.ok) {
          console.warn(`[SessionPage] /api/v1/images/random returned HTTP ${r.status} — falling back to solid pebbles`)
          return Promise.resolve([])
        }
        return r.json() as Promise<GroundingImage[]>
      })
      .then(data => setBgImages(prev => prev.length === 0 ? data : prev))
      .catch(err => {
        if (err instanceof Error && err.name === 'AbortError') return
        console.warn('[SessionPage] Background image fetch failed — pebbles will use solid colours:', err)
        void trackApiError(err instanceof Error ? err : new Error(String(err)), '/api/v1/images/random')
      })
    return () => controller.abort()
  }, [])

  return (
    <SessionProvider>
      <GreenPageLayout images={bgImages} showPebbles={showPebbles}>
        <SessionFlow
          facilitatorId={facilitatorId}
          showAmbient={fromEventCode}
          onGroundingImagesReady={handleGroundingImagesReady}
          onStepChange={handleStepChange}
        />
      </GreenPageLayout>
    </SessionProvider>
  )
}

export default SessionPage
